import { BehaviorSubject, Observable, Subject, of, zip } from 'rxjs';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { DataLoader } from '../util/data-loader';
import { DataSource, DataSourceField, SortBy } from '../model/data-source';
import { mutateDataArray } from './data-source-util';
import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { RestResponse } from '../model/rest-response';
import { RestService } from '../service/rest.service';
import { RequestCriteria } from '../model/request-criteria';
import { TranslateService } from '@ngx-translate/core';
import { generateRandomHash } from './data-util';
import { transformDataToClass } from './rest-service-util';


@Injectable()
export abstract class RestDataLoader<T> extends RestService implements DataLoader<T> {

  protected dataSubject = new BehaviorSubject<T[]>([]);
  protected loadingSubject = new BehaviorSubject<boolean>(null);
  protected dataCountSubject = new BehaviorSubject<number>(0);
  protected errorSubject = new BehaviorSubject<any>(null);
  private cancelSubject = new Subject<void>();
  data$: Observable<T[]>;
  loading$: Observable<boolean>;
  dataCount$: Observable<number>;
  error$?: Observable<any>;
  protected mutableFields: DataSourceField[];

  constructor(http: HttpClient, protected translate: TranslateService) {
    super(http);
    this.data$ = this.dataSubject.asObservable();
    this.loading$ = this.loadingSubject.asObservable();
    this.dataCount$ = this.dataCountSubject.asObservable();
    this.error$ = this.errorSubject.asObservable();
  }

  init(dataSource: DataSource): void {
    this.mutableFields = dataSource.getMutatorFields();
  }

  clearData(): void {
    this.dataSubject.next([]);
    this.dataCountSubject.next(0);
  }

  loadTableData(startRow: number, endRow: number, dataPageSize: number, filters: RequestCriteria, sorters?: SortBy[]): void {
    const payload = this.prepareRestRequest(startRow, endRow, dataPageSize, filters, sorters);
    this.sendRequest(payload);
  }

  fetchPage(startRow: number, endRow: number, filters: RequestCriteria, sorters?: SortBy[]): Observable<{ data: T[]; totalRows: number }> {
    const payload = this.prepareRestRequest(startRow, endRow, endRow - startRow, filters, sorters);
    return this.fetchRequest(payload).pipe(
      map(response => {
        if (response.data) mutateDataArray(response.data, this.mutableFields);
        return { data: response.data ?? [], totalRows: response.totalRows };
      })
    );
  }

  protected getRestFetchEndpoint(): string | null { return null; }

  protected getEntityType(): any { return null; }

  /** For loaders that need to mutate the fetch payload (extra criteria etc.) - applied on every fetch path, including the streamed export. */
  protected transformFetchPayload(payload: any): any { return payload; }

  /** For loaders that need extra request headers - applied on every fetch path, including the streamed export. */
  protected getFetchHeaders(): HttpHeaders | null { return null; }

  /**
   * Default fetch implementation for the common case: POST the payload to
   * getRestFetchEndpoint() and deserialize into getEntityType(). Prefer the
   * transformFetchPayload/getFetchHeaders hooks over overriding this method -
   * an override cuts the loader off from the streamed "export all" path
   * (fetchWithProgress), which builds its request from the same hooks.
   */
  protected fetchRequest(payload: any): Observable<RestResponse<T>> {
    const endpoint = this.getRestFetchEndpoint();
    if (!endpoint) throw new Error('fetchRequest() has no default endpoint - override getRestFetchEndpoint() or fetchRequest() itself.');
    return this.postRequest<T>(this.getEntityType(), endpoint, this.transformFetchPayload(payload), this.getFetchHeaders());
  }

  fetchWithProgress(filters: RequestCriteria, sorters: SortBy[] | undefined, limit: number): Observable<HttpEvent<any>> | null {
    const endpoint = this.getRestFetchEndpoint();
    if (!endpoint) return null;
    const body = this.transformFetchPayload(this.prepareRestRequest(0, limit, limit, filters, sorters));
    return this.http.post(`${this.serverUrl}${endpoint}`, body, {
      observe: 'events',
      reportProgress: true,
      responseType: 'arraybuffer',
      headers: this.getFetchHeaders() ?? undefined
    }) as unknown as Observable<HttpEvent<any>>;
  }

  applyMutations(data: any[]): void {
    const entityType = this.getEntityType();
    if (entityType) transformDataToClass(data, entityType);
    mutateDataArray(data, this.mutableFields);
  }

  cancel(): void {
    this.cancelSubject.next();
  }

  protected sendRequest(payload: any): void {
    this.loadingSubject.next(true);

    this.fetchRequest(payload)
      .pipe(
        takeUntil(this.cancelSubject),
        map(response => {
          if (response.data)
            mutateDataArray(response.data, this.mutableFields);
          return response;
        }),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe(
        response => {
          const withRowId = response.data.map(x => Object.assign(x, { rowId: generateRandomHash(15) }));
          this.dataSubject.next(withRowId);
          this.dataCountSubject.next(response.totalRows);
        },
        error => {
          console.error(error);
          this.dataSubject.next([]);
          this.dataCountSubject.next(0);
          this.transformError(error).subscribe(transformedError => this.errorSubject.next(transformedError));
        }
      );
  }

  protected getErrorTransitions(): any {
    return {};
  }

  private transformError(error: any): Observable<any> {
    if (error.errors && Object.values(error.errors).length && Object.keys(this.getErrorTransitions()).length) {
      const transitions = this.getErrorTransitions();
      const translateObservables = [];
      Object.keys(transitions).forEach(key => translateObservables.push(this.translate.get(key, transitions[key])));
      return zip(...translateObservables)
        .pipe(map(result => {
          const resultKeys = Object.keys(result);
          Object.keys(transitions).forEach((transitionKey, index) => {
            const foundKey = Object.keys(error.errors).find(key => error.errors[key] == transitionKey);
            error.errors[foundKey] = result[resultKeys[index]];
          });
          return error;
        }));
    } else {
      return of(error);
    }
  }

}
